/**
 * Payment Method Card Component
 * Displays a saved card with actions (set default, delete)
 */

import { useState } from 'react'
import type { PaymentMethod } from '../../types/paymentMethod'

interface PaymentMethodCardProps {
  paymentMethod: PaymentMethod
  onSetDefault: (id: string) => Promise<void>
  onDelete: (id: string) => Promise<void>
}

const CARD_BRAND_COLORS: Record<string, string> = {
  visa: 'bg-blue-500',
  mastercard: 'bg-orange-500',
  amex: 'bg-green-500',
  discover: 'bg-sky-500',
  default: 'bg-gray-500'
}

export default function PaymentMethodCard({
  paymentMethod,
  onSetDefault,
  onDelete
}: PaymentMethodCardProps) {
  const [isDeleting, setIsDeleting] = useState(false)
  const [isSetting, setIsSetting] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const formatExpiration = (month: number, year: number) => {
    return `${month.toString().padStart(2, '0')}/${year.toString().slice(-2)}`
  }

  const isExpired = (month: number, year: number) => {
    const now = new Date()
    const currentYear = now.getFullYear()
    const currentMonth = now.getMonth() + 1
    return year < currentYear || (year === currentYear && month < currentMonth)
  }

  const capitalizeFirst = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1)
  }

  const getBrandColor = (brand: string) => {
    return CARD_BRAND_COLORS[brand.toLowerCase()] || CARD_BRAND_COLORS.default
  }

  const handleSetDefault = async () => {
    if (paymentMethod.is_default) return

    setIsSetting(true)
    try {
      await onSetDefault(paymentMethod.id)
    } finally {
      setIsSetting(false)
    }
  }

  const handleDelete = async () => {
    setIsDeleting(true)
    try {
      await onDelete(paymentMethod.id)
    } catch (error) {
      setIsDeleting(false)
      setShowDeleteConfirm(false)
    }
  }

  const expired = isExpired(paymentMethod.exp_month, paymentMethod.exp_year)

  return (
    <div className={`
      border rounded-lg p-4 transition-all
      ${expired ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-white'}
      ${paymentMethod.is_default ? 'ring-2 ring-[#5BB4D6]' : ''}
    `}>
      <div className="flex items-start justify-between">
        {/* Card info */}
        <div className="flex items-start gap-3">
          {/* Card brand icon */}
          <div className={`
            w-12 h-8 rounded flex items-center justify-center text-white font-bold text-xs
            ${getBrandColor(paymentMethod.brand)}
          `}>
            {paymentMethod.brand.slice(0, 4).toUpperCase()}
          </div>

          {/* Card details */}
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-gray-900">
                {capitalizeFirst(paymentMethod.brand)} •••• {paymentMethod.last4}
              </h3>
              {paymentMethod.is_default && (
                <span className="px-2 py-0.5 text-xs font-medium bg-[#D4EEFF] text-[#2C7799] rounded">
                  Default
                </span>
              )}
              {expired && (
                <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded">
                  Expired
                </span>
              )}
            </div>
            <p className="text-sm text-gray-600 mt-1">
              Expires {formatExpiration(paymentMethod.exp_month, paymentMethod.exp_year)}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Added {new Date(paymentMethod.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          {/* Set as default button */}
          {!paymentMethod.is_default && !expired && (
            <button
              onClick={handleSetDefault}
              disabled={isSetting}
              className="px-3 py-1.5 text-sm font-medium text-[#3B8DB3] hover:text-[#2C7799] hover:bg-[#F0F8FF] rounded transition-colors disabled:opacity-50"
            >
              {isSetting ? 'Setting...' : 'Set Default'}
            </button>
          )}

          {/* Delete button */}
          {!showDeleteConfirm ? (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isDeleting}
              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
              title="Delete card"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          ) : (
            <div className="flex items-center gap-1">
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="px-2 py-1 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded disabled:opacity-50"
              >
                {isDeleting ? 'Deleting...' : 'Confirm'}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
                className="px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Warning for expired cards */}
      {expired && (
        <div className="mt-3 p-2 bg-red-100 border border-red-200 rounded text-sm text-red-800">
          This card has expired. Please add a new card and remove this one.
        </div>
      )}
    </div>
  )
}
